<template>
  <div class="flex flex-col gap-4">
    <h2 class="text-2xl font-bold text-blue">
      Submit your details directly to {{ agency.Name }}
    </h2>

    <h2 class="text-sm md:text-3xl font-bold text-blue">Your Job Preferences:</h2>

    <div class="flex">
      <div
          class="hidden md:flex items-center w-52 bg-grey-15 border border-grey-25 px-4 border-r-0 rounded-l text-sm text-grey-90"
      >
        Desired Job Role
      </div>
      <InputText
          class="rounded-l md:rounded-l-none rounded-r text-grey-90"
          name="preferredJobRole"
          placeholder="Preferred job role"
          :value="preferredJobRole"
          :error="errors.hasOwnProperty('formFields.preferredJobRole')"
          :error-message="getErrorMessage(errors, 'formFields.preferredJobRole')"
          @change="(event) =>
            $emit('changeParameter', {
              key: 'preferredJobRole',
              value: event.target.value,
            })
          "
      />
    </div>

    <div class="flex">
      <div
          class="hidden md:flex items-center w-52 bg-grey-15 border border-grey-25 px-4 border-r-0 rounded-l text-sm text-grey-90"
      >
        Desired Job Location
      </div>
      <InputText
          class="rounded-l md:rounded-l-none rounded-r text-grey-90"
          name="preferredLocation"
          placeholder="Desired job location"
          :value="preferredLocation"
          :error="errors.hasOwnProperty('formFields.preferredLocation')"
          :error-message="getErrorMessage(errors, 'formFields.preferredLocation')"
          @change="
          (event) =>
            $emit('changeParameter', {
              key: 'preferredLocation',
              value: event.target.value,
            })
          "
      />
    </div>

    <h3 class="text-sm md:text-2xl font-bold text-blue">Salary Expectations:</h3>
    <div class="flex flex-col justify-between gap-4 sm:flex-row sm:gap-0">
      <div class="w-full">
        <span class="sr-only">Pay Period</span>
        <InputRadio
            class="text-grey-70"
            label="Annual Salary"
            value="annual"
            name="salaryExpectationUnit"
            :checked="salaryExpectationUnit === 'annual'"
            @change="(event) => onSalaryExpectationUnitSelect(event.target.value)"
        />
        <InputRadio
            class="text-grey-70"
            label="Hourly Rate"
            value="hourly"
            name="salaryExpectationUnit"
            :checked="salaryExpectationUnit === 'hourly'"
            @change="(event) => onSalaryExpectationUnitSelect(event.target.value)"
        />
      </div>

      <label class="w-full text-grey-70">
        Salary Expectation
        <InputDropdown
            placeholder="Please select"
            name="salaryExpectationValue"
            no-items-label="Please select a payment frequency"
            :items="salaryOptions[salaryExpectationUnit] ?? {}"
            :value="salaryExpectationValue"
            :key="`${salaryExpectationUnit}-${uuid}`"
            @change="
              (value) =>
                $emit('changeParameter', {
                  key: 'salaryExpectationValue',
                  value,
                })
            "
        />
        <span
            v-if="'formFields.salaryExpectationValue' in errors"
            class="-mt-2 text-sm text-red-600"
        >{{
            getErrorMessage(errors, 'formFields.salaryExpectationValue')
          }}</span
        >
      </label>
    </div>

    <h3 class="text-sm md:text-2xl font-bold text-blue">Your Message to the agency:</h3>
    <label>
      <span class="sr-only">Your Message to the Agency</span>
      <InputTextArea
          name="enquiry"
          rows="3"
          placeholder="Additional information about you, your current role and desired role"
          :value="emailContent"
          :error="errors.hasOwnProperty('formFields.emailContent')"
          @change="
          (event) =>
            $emit('changeParameter', {
              key: 'emailContent',
              value: event.target.value,
            })
        "
      />
    </label>
    <span
        v-if="'formFields.emailContent' in errors"
        class="-mt-2 text-sm text-red-600"
    >{{ getErrorMessage(errors, 'formFields.emailContent') }}</span
    >

    <div class="flex flex-row gap-4 items-center">
      <h2 class="text-sm md:text-2xl font-bold text-blue">Attach a CV (Optional):</h2>
      <label>
        <InputFile
            name="cvFile"
            :value="cvFile"
            :error="errors.hasOwnProperty('formFields.cvFile')"
            :error-message="getErrorMessage(errors, 'formFields.cvFile')"
            @change="
            (event) =>
              $emit('changeParameter', {
                key: 'cvFile',
                value: event.target.files[0],
              })
          "
        />
      </label>
    </div>
    <HorizontalSeparator class="my-4"/>
    <h2 class="text-sm md:text-3xl font-bold text-blue">Your Details:</h2>
    <div class="flex flex-col items-stretch justify-between gap-4 sm:flex-row">
      <div class="flex w-full">
        <div
            class="hidden md:flex items-center w-52 bg-grey-15 border border-grey-25 px-4 border-r-0 rounded-l text-sm text-grey-90"
        >
          First Name
        </div>
        <InputText
            class="rounded-l md:rounded-l-none rounded-r text-grey-90"
            name="firstName"
            placeholder="First name"
            :value="firstName"
            :error="errors.hasOwnProperty('formFields.firstName')"
            :error-message="getErrorMessage(errors, 'formFields.firstName')"
            @change="
            (event) =>
              $emit('changeParameter', {
                key: 'firstName',
                value: event.target.value,
              })
          "
        />
      </div>
      <div class="flex w-full">
        <div
            class="hidden md:flex items-center w-52 bg-grey-15 border border-grey-25 px-4 border-r-0 rounded-l text-sm text-grey-90"
        >
          Last Name
        </div>
        <InputText
            class="rounded-l md:rounded-l-none rounded-r text-grey-90"
            name="surname"
            placeholder="Last name"
            :value="surname"
            :error="errors.hasOwnProperty('formFields.surname')"
            :error-message="getErrorMessage(errors, 'formFields.surname')"
            @change="
            (event) =>
              $emit('changeParameter', {
                key: 'surname',
                value: event.target.value,
              })
          "
        />
      </div>
    </div>

    <div class="flex">
      <div
          class="hidden md:flex items-center w-52 bg-grey-15 border border-grey-25 px-4 border-r-0 rounded-l text-sm text-grey-90"
      >
        Current Job Role
      </div>
      <InputText
          class="rounded-l md:rounded-l-none rounded-r text-grey-90"
          name="jobTitle"
          placeholder="Current job role"
          :value="jobTitle"
          :error="errors.hasOwnProperty('formFields.jobTitle')"
          :error-message="getErrorMessage(errors, 'formFields.jobTitle')"
          @change="
          (event) =>
            $emit('changeParameter', {
              key: 'jobTitle',
              value: event.target.value,
            })
        "
      />
    </div>

    <div class="flex">
      <div
          class="hidden md:flex items-center w-52 bg-grey-15 border border-grey-25 px-4 border-r-0 rounded-l text-sm text-grey-90"
      >
        Email Address
      </div>
      <InputText
          class="rounded-l md:rounded-l-none rounded-r text-grey-90"
          name="emailAddress"
          placeholder="Email address"
          :value="emailAddress"
          :error="errors.hasOwnProperty('formFields.emailAddress')"
          :error-message="getErrorMessage(errors, 'formFields.emailAddress')"
          @change="
          (event) =>
            $emit('changeParameter', {
              key: 'emailAddress',
              value: event.target.value,
            })
        "
      />
    </div>

    <div class="flex">
      <div
          class="hidden md:flex items-center w-52 bg-grey-15 border border-grey-25 px-4 border-r-0 rounded-l text-sm text-grey-90"
      >
        Phone Number
      </div>
      <InputText
          class="rounded-l md:rounded-l-none rounded-r text-grey-90"
          name="telephoneNumber"
          placeholder="Your phone number"
          :value="telephoneNumber"
          :error="errors.hasOwnProperty('formFields.telephoneNumber')"
          :error-message="getErrorMessage(errors, 'formFields.telephoneNumber')"
          @change="
          (event) =>
            $emit('changeParameter', {
              key: 'telephoneNumber',
              value: event.target.value,
            })
        "
      />
    </div>

    <div class="flex w-full flex-col gap-4 lg:w-3/12">
      <InputButton style-type="contact-form" @click="submitForm">
        Send email
      </InputButton>
    </div>
  </div>
</template>

<script>
import HorizontalSeparator from 'Components/HorizontalSeparator';
import InputButton from 'Components/InputButton';
import InputText from 'Components/InputText';
import InputDropdown from 'Components/InputDropdown';
import InputTextArea from 'Components/InputTextArea';
import {getErrorMessage} from 'Helpers/helpers';
import InputFile from 'Components/InputFile';
import InputRadio from 'Components/InputRadio';
import candidateSalaryOptions from 'Mixins/candidateSalaryOptions';
import uuid from 'Mixins/uuid';
import { mapActions } from 'vuex';

export default {
  name: 'ContactCandidateEmailForm',
  components: {
    InputRadio,
    InputButton,
    InputFile,
    InputTextArea,
    InputDropdown,
    InputText,
    HorizontalSeparator,
  },
  mixins: [candidateSalaryOptions, uuid],
  props: {
    agency: {
      type: Object,
    },
    errors: {
      type: Object,
      default() {
        return {};
      },
    },
    preferredJobRole: {
      type: String,
    },
    preferredLocation: {
      type: String,
    },
    emailContent: {
      type: String,
    },
    firstName: {
      type: String,
    },
    surname: {
      type: String,
    },
    jobTitle: {
      type: String,
    },
    emailAddress: {
      type: String,
    },
    telephoneNumber: {
      type: String,
    },
    salaryExpectationValue: {
      type: String,
    },
    cvFile: {
      type: String,
    },
  },
  methods: {
    ...mapActions('contact', ['loadPersistentInformation', 'registerContact']),
    getErrorMessage,
    submitForm() {
      this.registerContact()
          .then((response) => {
            console.log('Form Submitted Successfully', response);
            this.$emit('submitForm');
          })
          .catch((error) => {
            console.error('Error submitting form', error);
          })
      ;
    }
  },
  mounted() {
    this.loadPersistentInformation('EmailCandidate');
  }
};
</script>
